import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Bookmark, BookmarkCheck, Check, RotateCcw, Sparkles, ThumbsDown } from "lucide-react";

import { buyerParts, withLiveDeadline } from "../Copilot/CopilotTenderResultsCard";
import { deadlineUrgency, formatDaysRemaining, formatDeadline } from "../../../services/tendersApi";
import { DocumentCount, NoticePill, SourceBadge, URGENCY_TEXT } from "./TenderUI";

/* One tender Discover found. Built from the design's card, with one deliberate
 * change: the ring counts requirement checks that pass ("4/5") rather than a
 * fit percentage. Every check is a line in the drawer a reader can verify; a
 * "94% fit" would be a number nobody could. */

export const DISMISS_REASONS = [
  { value: "not_our_work", label: "Not our kind of work" },
  { value: "cant_meet", label: "Can't meet a requirement" },
  { value: "wrong_place", label: "Wrong location" },
  { value: "size", label: "Too big or too small" },
  { value: "other", label: "Something else" },
];

const BAND_STYLE = {
  Strong: { ring: "#A04A97", text: "text-[#8E4187]" },
  Good: { ring: "#C98BC3", text: "text-[#9D5A96]" },
  Possible: { ring: "#B4AEB8", text: "text-[#6B6472]" },
};

export function FitRing({ band, passed = 0, total = 0, size = 64 }) {
  const style = BAND_STYLE[band] || BAND_STYLE.Possible;
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const share = total ? passed / total : 0;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F1E6EF" strokeWidth="6" />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={style.ring}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={`${c * share} ${c}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>
        <span className="absolute inset-0 grid place-items-center text-center leading-none">
          <span>
            <span className="block text-[17px] font-bold text-[#18101C]">
              {passed}/{total}
            </span>
            <span className="block text-[9.5px] font-medium text-[#77707E]">checks</span>
          </span>
        </span>
      </div>
      <span className={`text-[12px] font-semibold ${style.text}`}>{band ? `${band} fit` : "Unrated"}</span>
    </div>
  );
}

export function WhyChips({ why = [], limit = 4 }) {
  if (!why.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {why.slice(0, limit).map((chip) =>
        chip.kind === "gap" ? (
          <span
            key={`${chip.kind}-${chip.text}`}
            className="inline-flex items-center gap-1 rounded-md border border-[#F5C46B]/60 bg-[#FFF8E6] px-2 py-[3px] text-[12.5px] text-[#92400E]"
          >
            <AlertTriangle className="h-3 w-3" strokeWidth={2.4} />
            {chip.text}
          </span>
        ) : (
          <span
            key={`${chip.kind}-${chip.text}`}
            className="inline-flex items-center gap-1 rounded-md border border-[#BFE5C9] bg-[#F1FAF3] px-2 py-[3px] text-[12.5px] text-[#1F6B33]"
          >
            <Check className="h-3 w-3" strokeWidth={2.6} />
            {chip.text}
          </span>
        )
      )}
    </div>
  );
}

export function DismissMenu({ onPick, onClose, up = false }) {
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  return (
    <div
      ref={ref}
      role="menu"
      className={`absolute right-0 z-20 w-[220px] overflow-hidden rounded-xl border border-[#18101C]/10 bg-white py-1 shadow-[0_14px_32px_-14px_rgba(24,16,28,0.35)] ${
        up ? "bottom-full mb-1.5" : "top-full mt-1.5"
      }`}
    >
      <p className="px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#77707E]">
        Why isn't it a fit?
      </p>
      {DISMISS_REASONS.map((r) => (
        <button
          key={r.value}
          type="button"
          role="menuitem"
          onClick={() => onPick(r.value)}
          className="block w-full px-3 py-1.5 text-left text-[13.5px] text-[#18101C] hover:bg-[#A04A97]/[0.07] focus:bg-[#A04A97]/[0.07] focus:outline-none"
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

// `compact` while a tender is open beside the list: the closing date and the
// buttons move under the title, so the title keeps the card's full width
// instead of wrapping a word to a line.
export default function DiscoverCard({ tender, active, compact = false, onOpen, onSave, onDismiss, onRestore }) {
  const t = withLiveDeadline(tender);
  const m = t.match || {};
  const [menuOpen, setMenuOpen] = useState(false);
  const urgency = deadlineUrgency(t);
  const open = () => onOpen?.(tender);
  const stop = (e) => e.stopPropagation();
  const reasonLabel = DISMISS_REASONS.find((r) => r.value === m.dismiss_reason)?.label;
  // "In 162 days", or "Closes today" / "Closed" / "No deadline" as they are.
  const remaining = formatDaysRemaining(t);
  const counting = remaining.endsWith(" left");
  const closesIn = !remaining ? "No deadline" : counting ? `In ${remaining.replace(" left", "")}` : remaining;
  const closesLine = counting ? `Closes ${closesIn.toLowerCase()}` : closesIn;
  const actions = (
    <CardActions
      m={m}
      tender={tender}
      menuOpen={menuOpen}
      setMenuOpen={setMenuOpen}
      onSave={onSave}
      onDismiss={onDismiss}
      onRestore={onRestore}
      stop={stop}
    />
  );

  return (
    <div
      role="button"
      tabIndex={0}
      data-sensa-keep-panel
      onClick={open}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && e.target === e.currentTarget && open()}
      className={`group mb-3 flex cursor-pointer gap-5 rounded-2xl border bg-white px-5 py-4 font-outfit transition-all ${
        active
          ? "border-[#A04A97] shadow-[0_2px_14px_rgba(160,74,151,0.16)]"
          : "border-[#E9DCE7] hover:border-[#C95EBE] hover:shadow-[0_2px_12px_rgba(201,94,190,0.12)]"
      }`}
    >
      <div className="w-[76px] flex-none pt-1">
        <FitRing band={m.band} passed={m.checks_passed} total={m.checks_total} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex flex-wrap items-center gap-[7px]">
          <SourceBadge label={t.source_label} />
          <NoticePill type={t.notice_type} label={t.notice_type_label} />
          {/* Discover shows notices without attachments too (a sources-sought
              is often its description alone), so say which this is, as the
              Tender Search row does. */}
          <DocumentCount hasDocuments={t.has_documents} count={t.document_count} linkCount={t.link_count} />
          {m.just_in ? (
            <span className="rounded-full bg-[#8E4187] px-2 py-[2px] text-[11px] font-semibold text-white">Just in</span>
          ) : (
            m.is_new && (
              <span className="rounded-full border border-[#A04A97]/40 px-2 py-[1px] text-[11px] font-semibold text-[#8E4187]">
                New
              </span>
            )
          )}
        </div>

        <h3 className="text-[16.5px] font-semibold leading-snug text-[#18101C] transition-colors group-hover:text-[#8E4187]">
          {t.title}
        </h3>
        <p className="mt-0.5 truncate text-[12.5px] uppercase tracking-wide text-[#71717A]">
          {[buyerParts(t).join(" › "), t.place, t.reference].filter(Boolean).join(" · ")}
        </p>

        {m.dismissed ? (
          <p className="mt-2.5 text-[13px] text-[#77707E]">Marked not a fit{reasonLabel ? `: ${reasonLabel}` : ""}.</p>
        ) : (
          <div className="mt-2.5">
            <p className="mb-1.5 flex items-center gap-1 text-[12.5px] font-semibold text-[#8E4187]">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2.2} />
              Why Sensa picked this
              {m.line_of_work && <span className="font-normal text-[#77707E]">· {m.line_of_work}</span>}
            </p>
            <WhyChips why={m.why} />
          </div>
        )}

        {compact && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-[#18101C]/[0.06] pt-3">
            <span className="text-[12.5px] text-[#77707E]">
              <span className={`font-semibold ${URGENCY_TEXT[urgency]}`}>{closesLine}</span>
              {t.closes_at && <> · {formatDeadline(t)}</>}
            </span>
            {actions}
          </div>
        )}
      </div>

      {!compact && (
        <div className="flex w-[170px] flex-none flex-col items-end justify-between text-right">
          <div>
            <span className="block text-[10.5px] font-semibold uppercase tracking-wide text-[#9CA3AF]">Closes</span>
            <span className={`block whitespace-nowrap text-[17px] font-semibold leading-tight ${URGENCY_TEXT[urgency]}`}>
              {closesIn}
            </span>
            <span className="mt-0.5 block text-[11.5px] text-[#9CA3AF]">{formatDeadline(t)}</span>
          </div>
          <div className="mt-3">{actions}</div>
        </div>
      )}
    </div>
  );
}

function CardActions({ m, tender, menuOpen, setMenuOpen, onSave, onDismiss, onRestore, stop }) {
  return (
    <div className="relative flex items-center gap-2" onClick={stop} onKeyDown={stop}>
      {m.dismissed ? (
        <button
          type="button"
          onClick={() => onRestore?.(tender)}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[#18101C]/15 bg-white px-3 text-[13px] font-semibold text-[#52525B] hover:bg-[#18101C]/[0.04]"
        >
          <RotateCcw className="h-3.5 w-3.5" strokeWidth={2.3} />
          Restore
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Not a fit"
            title="Not a fit"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="grid h-9 w-9 place-items-center rounded-lg border border-[#18101C]/15 bg-white text-[#6B6472] transition-colors hover:border-[#A04A97]/40 hover:text-[#8E4187]"
          >
            <ThumbsDown className="h-4 w-4" strokeWidth={2} />
          </button>
          {menuOpen && (
            <DismissMenu
              onClose={() => setMenuOpen(false)}
              onPick={(reason) => {
                setMenuOpen(false);
                onDismiss?.(tender, reason);
              }}
            />
          )}
          <button
            type="button"
            onClick={() => onSave?.(tender, !m.saved)}
            className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3.5 text-[13.5px] font-semibold transition-colors ${
              m.saved
                ? "border border-[#A04A97]/40 bg-[#A04A97]/[0.08] text-[#8E4187]"
                : "bg-[#A04A97] text-white hover:bg-[#8E4187]"
            }`}
          >
            {m.saved ? (
              <BookmarkCheck className="h-4 w-4" strokeWidth={2.2} />
            ) : (
              <Bookmark className="h-4 w-4" strokeWidth={2.2} />
            )}
            {m.saved ? "Saved" : "Save"}
          </button>
        </>
      )}
    </div>
  );
}
